/**
 * Per-browser "seen it" flag for the New View onboarding banner
 * (`NewViewOnboardingAlert`), stored in localStorage under
 * `dacw:onboardingDismissed`.
 *
 * - missing  -> banner shows (first visit to the new view)
 * - `"true"` -> banner stays hidden
 *
 * The flag is set when the user dismisses the banner *or* applies
 * their first filter (the hint has done its job), and cleared by the
 * Legacy "Open filters" affordance so the banner re-appears once to
 * walk them through the new layout.
 *
 * Reads `window.localStorage` directly, like `featureFlag.ts`, rather
 * than via `localStorageUtils` — that module imports the Katal logger,
 * which requires a browser `window` at import time and so can't be
 * unit-tested under Jest's node environment. Storage failures and
 * SSR degrade to "show the banner": a stray extra banner is
 * preferable to one that never appears.
 */

const ONBOARDING_DISMISSED_KEY = "dacw:onboardingDismissed";
const DISMISSED_VALUE = "true";

export function isOnboardingDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.localStorage.getItem(ONBOARDING_DISMISSED_KEY) === DISMISSED_VALUE
    );
  } catch {
    return false;
  }
}

export function markOnboardingDismissed(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ONBOARDING_DISMISSED_KEY, DISMISSED_VALUE);
  } catch {
    // storage blocked (private mode, enterprise policy): non-fatal.
  }
}

/**
 * Re-arm the banner. Used by the Legacy view's "Open filters" button
 * right before flipping to V2 so the user sees the how-to once more.
 * Uses `removeItem` (not a `"false"` write) so the state collapses
 * back to the default "missing = show" branch.
 */
export function resetOnboarding(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ONBOARDING_DISMISSED_KEY);
  } catch {
    // storage blocked: non-fatal, banner state stays whatever it was.
  }
}
